import React from 'react';
import { nen3140ScoreToConditionScore } from '../../../../common/src/domain/customInspectionTypes/nen3140/nen3140ScoreToConditionScore';
import { EditPanelCell, EditPanelRow, EditPanelSwitch, LeftTrayItem, ScrollMenuItem } from '../../components/elements';
import { ISurvey } from '../../components/Surveys/Interfaces/ISurvey';
import { ISurveyTemplateElementGroupData } from '../../components/Surveys/ISurveyTemplateElementGroupData';
import { ISurveyTemplateManifestationData } from '../../components/Surveys/ISurveyTemplateManifestationData';
import { ISurveyTemplateUnitData } from '../../components/Surveys/ISurveyTemplateUnitData';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchJson } from '../../store/fetch';
import { ICustomInspectionTypeFrontEndSettings } from '../ICustomInspectionTypeFrontEndSettings';
import { Nen3140ConditionScoreLabel } from './Nen3140ConditionScoreLabel';
import { nen3140SelectOptions } from './nen3140SelectOptions';
import { SurveyDetailsNen3140 } from './SurveyDetailsNen3140';
import { SurveyDetailsNen3140SummaryAndAdvice } from './SurveyDetailsNen3140SummaryAndAdvice';
import { SurveyEditNen3140Result } from './SurveyEditNen3140Result';
import { SurveyEditNen3140SummaryAndAdvice } from './SurveyEditNen3140SummaryAndAdvice';

const tabNames = {
    nen3140Result: 'nen3140Result',
    summaryAndAdvice: 'summaryAndAdvice',
    manageVisuals: 'manageVisuals',
    videos: 'videos',
    documents: 'documents',
    workflow: 'workflow',
};

function SurveyEditPage(
    props: { tabName: string; surveyId: string; objectId: string; onItemChanged: () => Promise<void> } & (
        | ISurveyTemplateUnitData
        | ISurveyTemplateElementGroupData
        | ISurveyTemplateManifestationData
    ),
) {
    switch (props.tabName) {
        case tabNames.nen3140Result: {
            return <SurveyEditNen3140Result {...props} />;
        }
        case tabNames.manageVisuals:
        case tabNames.videos:
        case tabNames.documents:
        case tabNames.workflow:
        case tabNames.summaryAndAdvice:
            return null;
        default:
            return <h1>Unknown tab &quot;${props.tabName}&quot;</h1>;
    }
}

function DecompositionUnitAddEdit(props: { inspectionStandardData?: any; handleChange: (value: any, stateKey: string) => unknown }) {
    const translate = useTranslate();
    return (
        <EditPanelRow>
            <EditPanelCell rowCount={12}>
                <EditPanelSwitch
                    name="unitInspectionStandardData.nen3140.isElectricalInstallation"
                    placeholder={translate('nen3140.isElectricalInstallation')}
                    handleChange={props.handleChange}
                    value={props.inspectionStandardData?.nen3140?.isElectricalInstallation ?? false}
                />
            </EditPanelCell>
        </EditPanelRow>
    );
}

function SurveyDetailsLeftTrayListItems(props: { survey: ISurvey }) {
    const translate = useTranslate();
    return (
        <LeftTrayItem title={translate('nen3140.nen3140Result')}>
            <Nen3140ConditionScoreLabel conditionScore={props.survey.condition} />
        </LeftTrayItem>
    );
}

function SurveyDetailsScrollMenuItems(props: { scrollTo: (name: string) => void }) {
    const translate = useTranslate();
    return (
        <>
            <ScrollMenuItem text={translate('nen3140.nen3140')} icon="fa fa-bolt" onClick={() => props.scrollTo('Nen3140Report')} />
            <ScrollMenuItem text="Samenvatting en advies" icon="fa fa-file-text-o" onClick={() => props.scrollTo('SummaryAndAdvice')} />
        </>
    );
}

function SurveyDetailsComponents(props: { survey: Required<ISurvey> }) {
    return (
        <>
            <SurveyDetailsNen3140 surveyId={props.survey.id} />
            <SurveyDetailsNen3140SummaryAndAdvice survey={props.survey} />
        </>
    );
}

function SurveyEditSummaryAndAdvice(props: { surveyId: string }) {
    return <SurveyEditNen3140SummaryAndAdvice surveyId={props.surveyId} />;
}

function ConditionLabel(props: { conditionScore?: string }): JSX.Element | null {
    return <Nen3140ConditionScoreLabel conditionScore={props.conditionScore} />;
}

function DecompositionElementAddEdit(props: { inspectionStandardData?: any; handleChange: (value: any, stateKey: string) => unknown }) {
    const translate = useTranslate();
    return (
        <EditPanelRow>
            <EditPanelCell rowCount={12}>
                <EditPanelSwitch
                    name="elementInspectionStandardData.nen3140.isElectricalInstallation"
                    placeholder={translate('nen3140.isElectricalInstallation')}
                    handleChange={props.handleChange}
                    value={props.inspectionStandardData?.nen3140?.isElectricalInstallation ?? false}
                />
            </EditPanelCell>
        </EditPanelRow>
    );
}

export const nen3140FrontEndSettings: ICustomInspectionTypeFrontEndSettings = {
    tabNames,

    surveyEditTabs: [
        { name: tabNames.videos, textTranslateKey: 'survey.videosTab' },
        { name: tabNames.manageVisuals, textTranslateKey: 'common.visuals' },
        { name: tabNames.nen3140Result, textTranslateKey: 'nen3140.nen3140Result' },
        { name: tabNames.workflow, textTranslateKey: 'workflow.workflow' },
        { name: tabNames.documents, textTranslateKey: 'survey.documents' },
        { name: tabNames.summaryAndAdvice, textTranslateKey: 'survey.summaryAndAdviceTab' },
    ],

    surveyEditDefaultTabName: tabNames.nen3140Result,
    showDocuments: true,
    objectTreeOptions: {
        hasElementsGroups: false,
        archiveLinkAvailable: false,
    },

    objectTreeDefaults: {
        filterOnElementGroups: false,
    },

    getSurveyEditPage: SurveyEditPage,
    getSurveyDetailsLeftTrayListItems: SurveyDetailsLeftTrayListItems,
    getSurveyDetailsScrollMenuItems: SurveyDetailsScrollMenuItems,
    getSurveyDetailsComponents: SurveyDetailsComponents,
    getSurveyEditSummaryAndAdvice: SurveyEditSummaryAndAdvice,
    getConditionLabel: ConditionLabel,
    getDecompositionElementAddEdit: DecompositionElementAddEdit,
    getDecompositionUnitAddEdit: DecompositionUnitAddEdit,

    showObjectTree: (tabName: string) => tabName === tabNames.manageVisuals || tabName === tabNames.nen3140Result,

    surveyEditLoadElements: async (surveyId: string) => {
        const elementsResponse = await fetchJson(`/api/surveys/${surveyId}/elements/nen3140/objectTree`, 'GET');
        const elements = elementsResponse.json;
        return {
            elements,
        };
    },

    objectTreeFilter: {
        translationKey: 'nen3140.filterOnElectricalInstallation',
        elementFilter: (element) => element.inspectionStandardData?.nen3140?.isElectricalInstallation === true,
        unitFilter: (unit) => unit.inspectionStandardData?.nen3140?.isElectricalInstallation === true,
    },

    getSurveyConditionFilterOptions: () =>
        nen3140SelectOptions.nen3140Score.map((i) => ({ value: nen3140ScoreToConditionScore(i.value as any), text: i.text })),

    showLabelInSurveysMap: true,
};
